import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Modal, useLocale } from '@ministry/ui/internal';

import { revokeRoleAssignment, type RoleAssignment } from '../../lib/admin-users.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { scopeLevelLabel } from '../../lib/admin-lifecycle.js';
import { roleLabel } from '../../lib/roles.js';
import { useT } from '../../i18n/strings.js';
import { grantRefusalKey } from './routes.js';

export interface RevokeAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  assignment: RoleAssignment | null;
  onSuccess: () => void;
}

/**
 * SCREEN_SPECS.md I19: "revoking an assignment needs a confirmation that names what is
 * being removed from whom." `decideRevoke` (`grant-authority.ts`) applies the same role
 * ceiling and scope/clearance rules as a grant (minus `SELF_GRANT`), so the same five
 * refusal codes can come back here too.
 */
export function RevokeAssignmentModal({
  open,
  onOpenChange,
  userId,
  userName,
  assignment,
  onSuccess,
}: RevokeAssignmentModalProps): React.JSX.Element | null {
  const t = useT();
  const { locale } = useLocale();

  const mutation = useMutation({
    mutationFn: () => {
      if (!assignment) throw new Error('No assignment selected.');
      return revokeRoleAssignment(userId, assignment.id);
    },
    onSuccess: () => {
      onOpenChange(false);
      onSuccess();
    },
  });

  if (!assignment) return null;

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    if (mutation.error instanceof ApiError) {
      const refusal = mutation.error.details?.refusal;
      if (typeof refusal === 'string') return <AlertBanner variant="critical" title={t(grantRefusalKey(refusal))} />;
      if (mutation.error.code === 'CONFLICT_STATE') {
        return <AlertBanner variant="warning" title={t('admin.revoke.error.alreadyEnded')} />;
      }
      return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
    }
    return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
  })();

  const anchorText = assignment.scopeOrgUnitCode ? ` · ${assignment.scopeOrgUnitCode}` : '';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.revoke.title')}
      description={t('admin.revoke.description', {
        role: roleLabel(assignment.roleCode, locale),
        scope: `${scopeLevelLabel(assignment.scopeLevel, locale)}${anchorText}`,
        name: userName,
      })}
      confirmLabel={mutation.isPending ? t('admin.revoke.submitting') : t('admin.revoke.submit')}
      cancelLabel={t('admin.common.cancel')}
      variant="destructive"
      loading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
    >
      {banner}
    </Modal>
  );
}
